import { findPhoto, loadCatalog, type Catalog, type CatalogPhoto } from './catalog';
import { initGL, type GLContext } from './gl/context';
import { flockAnchor, lightDirection, makeCamera, pointerToWorld, type Camera } from './render/camera';
import { analyzeSky, loadPhoto, type Photo } from './render/photo';
import { coverFit, photoToScreenY, screenToPhotoY, SceneRenderer, type BirdDrawOptions, type CoverFit } from './render/scene';
import { GRID } from './shaders/common';
import { decodeScene, encodeScene, sceneCodeFromLocation, sceneUrl } from './sim/codec';
import { FlockSim, type StepUniforms } from './sim/flock';
import { Flags, PARAM_BY_KEY, defaultParams, randomSeed, snap, type NumericParamKey, type SceneParams } from './sim/params';
import type { SkyAnalysis } from './render/photo';
import { Predator, type PredatorState, type Vec3 } from './sim/predator';
import { HorizonTool } from './ui/horizon';
import { Hud } from './ui/hud';
import { isTouchDevice, lang, setLang, t } from './ui/i18n';
import { Panel } from './ui/panel';

const SIM_DT = 1 / 60;
const MAX_STEPS_PER_FRAME = 2;
const MAX_DPR = 2;
const BIRD_COLOR: Vec3 = [0.05, 0.045, 0.05];
/** Fully lit wing tones: cool overcast grey to warm low-sun brown. Dim on purpose. */
const LIT_COOL: Vec3 = [0.3, 0.3, 0.32];
const LIT_WARM: Vec3 = [0.55, 0.38, 0.22];
/** Agitation wave speed target, m/s: 60 m in about 3 s. */
const WAVE_SPEED = 20;
/** Crossfade lengths in wall-clock seconds, and the bird fade back in. */
const CROSSFADE_USER_S = 2;
const CROSSFADE_KIOSK_S = 4;
const BIRDS_BACK_S = 1.5;
/** Sim steps run while the birds are invisible after a photo change, so they fade in already flocking. */
const PREROLL_STEPS = 180;
const PREROLL_PER_FRAME = 3;
const KIOSK_INTERVAL_MS = 180_000;
const KIOSK_IDLE_MS = 2000;
const IDLE_CURSOR_MS = 3000;
const HINT_KEY = 'murmurations.hinted';
const VISITS_KEY = 'murmurations.visits';
const HUD_KEY = 'murmurations.hud';
const HOLD_FOR_HINT_MS = 700;
const TOUCH_OFFSET_PX = 28;

interface FrameContext {
  fit: CoverFit;
  horizonScreen: number;
  cam: Camera;
  anchor: Vec3;
  spread: number;
}

/** A photo change in progress: crossfade the photos, dip the birds, re-place the flock. */
interface Transition {
  started: number;
  seconds: number;
  from: Photo | null;
  to: Photo;
  entry: CatalogPhoto;
  resetDefaults: boolean;
  reseed: boolean;
  switched: boolean;
  prerollLeft: number;
}

class App {
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: GLContext;
  private readonly gl: WebGL2RenderingContext;
  private readonly renderer: SceneRenderer;
  private sim: FlockSim;
  private readonly predator = new Predator();
  private falcon: PredatorState | null = null;
  private photo: Photo | null = null;
  private transition: Transition | null = null;
  private birdAlpha = 1;
  private readonly panel: Panel;
  private readonly hud: Hud;
  private readonly horizonTool: HorizonTool;
  private params: SceneParams;
  private paused = false;
  private debug = false;
  private hudOn = false;
  private panelForced = false;
  private simTime = 0;
  private accumulator = 0;
  private lastFrame = 0;
  private frameEma = 16.7;
  private qualityClock = 0;
  private fps = 60;
  private stepCount = 0;
  private stepWindowStart = 0;
  private stepsPerSec = 60;
  private urlTimer = 0;
  private photoLoading: string | null = null;
  private kioskClock = 0;
  private cursorTimer = 0;
  private enteredFullscreen = false;
  private wakeLock: WakeLockLike | null = null;
  private lastPointerType = 'mouse';
  private livePointer: number | null = null;
  private liveStart = 0;
  private readonly pointersDown = new Set<number>();
  private hinted = false;
  private hintShown = false;
  private sawAttack = false;
  /** Flock centre of mass from the GPU (asynchronous readback); null until known. */
  private flockCenter: Vec3 | null = null;

  constructor(ctx: GLContext, private readonly catalog: Catalog, params: SceneParams) {
    this.ctx = ctx;
    this.gl = ctx.gl;
    this.canvas = ctx.canvas;
    this.params = params;
    this.renderer = new SceneRenderer(ctx);
    this.sim = new FlockSim(ctx, Math.round(params.count), params.seed);
    this.resize();
    this.resetFlock(params.seed);
    // The overlay's own switch; until it has been touched it follows expert mode.
    this.hudOn = readFlag(HUD_KEY, readFlag('murmurations.expert', false));

    const ui = document.getElementById('ui')!;
    this.panel = new Panel(ui, this.params, catalog, {
      onParam: (k, v) => this.setParam(k, v),
      onPhoto: (id) => this.changePhoto(id, { resetDefaults: true, reseed: false, seconds: CROSSFADE_USER_S }),
      onReseed: () => this.reseed(),
      onShare: () => this.share(),
      onHorizon: () => this.startHorizon(),
      onPause: () => this.togglePause(),
      onKiosk: () => this.setKiosk(true, true),
      onFullscreen: () => this.toggleFullscreen(),
      onExpert: (expert) => this.onExpert(expert),
      onFlag: (flag, on) => this.setFlag(flag, on),
      onHud: (on) => this.setHud(on),
      hudOn: () => this.hudOn,
    });
    this.hud = new Hud(ui);
    this.horizonTool = new HorizonTool(
      ui,
      (sy) => {
        this.setSnapped('horizon', screenToPhotoY(this.frameContext().fit, sy));
        this.scheduleUrlUpdate();
      },
      () => this.reanalyseSky(),
    );

    window.addEventListener('keydown', (e) => this.onKey(e));
    window.addEventListener('hashchange', () => this.onHashChange());
    window.addEventListener('resize', () => this.horizonTool.set(photoToScreenY(this.frameContext().fit, this.params.horizon)));
    window.addEventListener('pointermove', () => this.onAnyPointerMove());
    document.addEventListener('fullscreenchange', () => this.onFullscreenChange());
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && this.kiosk) void this.requestWakeLock();
    });
    this.canvas.addEventListener('pointerdown', (e) => this.onCanvasDown(e));
    this.canvas.addEventListener('pointermove', (e) => this.onCanvasMove(e));
    this.canvas.addEventListener('pointerup', (e) => this.onCanvasUp(e));
    this.canvas.addEventListener('pointercancel', (e) => this.onCanvasUp(e));
    this.canvas.addEventListener('lostpointercapture', (e) => this.onCanvasUp(e));
    this.canvas.addEventListener('dblclick', () => {
      // Two quick taps on a touch screen also arrive as dblclick; there, two fingers leave kiosk.
      if (this.kiosk && this.lastPointerType !== 'touch') this.setKiosk(false);
    });
    // A lost context (GPU reset, memory pressure on a tablet left in kiosk) leaves every
    // GL call a silent no-op. The permalink restores the scene, so reload.
    this.canvas.addEventListener('webglcontextlost', (e) => {
      e.preventDefault();
      window.setTimeout(() => location.reload(), 1000);
    });
    // A long press must stay a long press: no context menu, no iOS callout, no pinch.
    this.canvas.addEventListener('contextmenu', (e) => e.preventDefault());
    this.canvas.addEventListener('touchmove', (e) => e.preventDefault(), { passive: false });
    this.canvas.addEventListener('gesturestart', (e) => e.preventDefault(), { passive: false } as AddEventListenerOptions);
  }

  async start(fromLink: boolean): Promise<void> {
    const entry = findPhoto(this.catalog, this.params.photo) ?? this.catalog.photos[0]!;
    try {
      this.photo = await loadPhoto(this.gl, entry.id, entry.file, this.params.horizon);
      // A link's explicit light wins; a link that never chose one follows the photo.
      if (!fromLink) this.applyPhotoDefaults(this.photo, entry, false);
      else if (this.params.lightAuto >= 0.5) this.applyLight(this.photo);
    } catch (err) {
      console.error(err);
    }
    // Every entry path places the flock from the real canvas and photo, not the
    // placeholders the constructor had to use.
    this.resize();
    this.resetFlock(this.params.seed);
    this.panel.refresh();
    this.applyVisibility();
    if (this.kiosk) {
      // Entered from a link: behave like the button did, minus the toast.
      this.armIdleCursor();
      void this.requestWakeLock();
    }
    this.lastFrame = performance.now();
    this.stepWindowStart = this.lastFrame;
    requestAnimationFrame((ts) => this.frame(ts));
  }

  // ---- per-frame geometry ------------------------------------------------

  private frameContext(): FrameContext {
    const p = this.params;
    const pw = this.photo?.width ?? 3;
    const ph = this.photo?.height ?? 2;
    const fit = coverFit(pw, ph, this.canvas.width, this.canvas.height);
    const horizonScreen = clamp(photoToScreenY(fit, p.horizon), 0.02, 0.98);
    const cam = makeCamera(this.canvas.width, this.canvas.height, horizonScreen);
    const anchor = flockAnchor(cam, horizonScreen, p.flockHeight, p.flockDistance);
    const active = Math.max(1, this.sim?.active ?? p.count);
    const spread = Math.cbrt(active) * lerp(1.2, 4.5, p.flockSpread);
    return { fit, horizonScreen, cam, anchor, spread };
  }

  private stepUniforms(fc: FrameContext, f: PredatorState | null): StepUniforms {
    const p = this.params;
    const halfXZ = clamp(fc.spread * 3, 120, 400);
    const halfY = clamp(fc.spread * 1.5, 60, 150);
    const a = fc.anchor;
    const boxMin: Vec3 = [a[0] - halfXZ, Math.max(0, a[1] - halfY), a[2] - halfXZ];
    // At the front only part of a neighbourhood has fired (mean agitation ~0.5
    // above the threshold); excitation must still reach 1 in the time the
    // front needs to cross one cell, or the wave dies out.
    const cellX = (halfXZ * 2) / GRID.fine.g[0];
    const waveRate = (WAVE_SPEED / cellX / 0.5) * (1 + 0.5 * p.nervousness);
    return {
      time: this.simTime,
      anchor: a,
      boxMin,
      boxSize: [halfXZ * 2, halfY * 2, halfXZ * 2],
      b1: [p.cohesion, p.separation, p.alignment, p.speed],
      b2: [p.nervousness, p.turnRate, p.flapRate, p.crowding],
      spread: fc.spread,
      predator: f && f.active ? [f.pos[0], f.pos[1], f.pos[2], f.radius] : [0, 0, 0, 0],
      predatorVel: f ? f.vel : [0, 0, 0],
      startle: f ? f.startle : [0, 0, 0, 0],
      waveRate,
      alarm: f ? f.alarm : 0,
      alarmFrom: f ? f.alarmFrom : a,
    };
  }

  // ---- frame loop --------------------------------------------------------

  private frame(ts: number): void {
    const dtMs = Math.min(100, ts - this.lastFrame);
    this.lastFrame = ts;
    // A tab switch or a one-off hitch is not sustained slowness: keep it out of
    // the average that drives the quality gate, or one long frame drops birds.
    const hitch = dtMs > 50;
    if (hitch) {
      this.qualityClock = 0;
    } else {
      this.frameEma = this.frameEma * 0.95 + dtMs * 0.05;
      this.fps = 1000 / Math.max(1, this.frameEma);
    }
    this.resize();
    const fc = this.frameContext();

    if (!this.paused) {
      this.accumulator = Math.min(this.accumulator + dtMs / 1000, SIM_DT * MAX_STEPS_PER_FRAME);
      let steps = 0;
      // Tolerance so 60 Hz gives exactly one step per frame and 120 Hz one per two,
      // instead of 0/2 pairs whenever timestamps land a hair short of the boundary.
      while (this.accumulator >= SIM_DT * 0.75 && steps < MAX_STEPS_PER_FRAME) {
        this.simStep(fc);
        this.accumulator -= SIM_DT;
        steps++;
      }
      if (this.accumulator < 0) this.accumulator = 0;
      // Pre-roll while the birds are invisible: they fade in already flocking.
      const tr = this.transition;
      if (tr && tr.switched && tr.prerollLeft > 0 && this.frameEma < 20 && document.visibilityState === 'visible') {
        for (let i = 0; i < PREROLL_PER_FRAME && tr.prerollLeft > 0; i++, tr.prerollLeft--) {
          this.simStep(fc);
          steps++;
        }
      }
      this.stepCount += steps;
      this.adaptQuality(dtMs);
    }
    if (ts - this.stepWindowStart >= 1000) {
      this.stepsPerSec = (this.stepCount * 1000) / (ts - this.stepWindowStart);
      this.stepCount = 0;
      this.stepWindowStart = ts;
    }

    this.updateTransition(ts);
    if (this.kiosk) this.tickKiosk(dtMs);
    this.maybeShowHint();

    // Draw.
    this.renderer.begin(this.canvas.width, this.canvas.height);
    const tr = this.transition;
    if (tr) {
      const u = clamp((ts - tr.started) / (tr.seconds * 1000), 0, 1);
      this.renderer.drawPhoto(tr.from, this.fitFor(tr.from));
      this.renderer.drawPhoto(tr.to, this.fitFor(tr.to), smoothstep(u));
    } else {
      this.renderer.drawPhoto(this.photo, fc.fit);
    }
    const opts = this.birdOptions();
    this.renderer.drawBirds(this.sim, fc.cam, opts);
    if (this.falcon) this.renderer.drawFalcon(fc.cam, this.falcon, opts);

    this.hud.update(ts, {
      fps: this.fps,
      frameMs: this.frameEma,
      stepsPerSec: this.stepsPerSec,
      active: this.sim.active,
      requested: Math.round(this.params.count),
      width: this.canvas.width,
      height: this.canvas.height,
      dpr: this.canvas.width / Math.max(1, this.canvas.clientWidth),
      renderer: this.ctx.renderer,
      fields: this.sim.fields32 ? '32F' : '16F',
      simTime: this.simTime,
      falcon: this.describeFalcon(),
      alarm: this.falcon?.alarm ?? 0,
      debug: this.debug,
    });
    requestAnimationFrame((t2) => this.frame(t2));
  }

  private simStep(fc: FrameContext): void {
    const f = this.predator.step(this.simTime, SIM_DT, {
      anchor: fc.anchor,
      spread: fc.spread,
      center: this.flockCenter ?? fc.anchor,
      rate: this.params.predatorRate,
      aggression: this.params.predatorAggression,
      nervousness: this.params.nervousness,
    });
    if (this.falcon?.active && !f.active && !f.live) this.sawAttack = true;
    this.falcon = f;
    const u = this.stepUniforms(fc, f);
    this.sim.step(SIM_DT, u);
    this.simTime += SIM_DT;
    // Asynchronous: issues a request or collects a finished one, never stalls.
    this.flockCenter = this.sim.readCenter(u) ?? this.flockCenter;
  }

  private birdOptions(): BirdDrawOptions {
    const p = this.params;
    const w = p.lightWarmth;
    return {
      birdSize: p.birdSize,
      hazeDensity: p.haze * 0.0035,
      birdColor: BIRD_COLOR,
      litColor: [lerp(LIT_COOL[0], LIT_WARM[0], w), lerp(LIT_COOL[1], LIT_WARM[1], w), lerp(LIT_COOL[2], LIT_WARM[2], w)],
      lightDir: lightDirection(p.lightAngle, w),
      hazeColor: this.photo?.hazeColor ?? [0.7, 0.7, 0.75],
      alpha: this.birdAlpha,
      debug: this.debug,
    };
  }

  private fitFor(photo: Photo | null): CoverFit {
    return coverFit(photo?.width ?? 3, photo?.height ?? 2, this.canvas.width, this.canvas.height);
  }

  private describeFalcon(): string {
    const f = this.falcon;
    if (!f) return 'off';
    if (f.live) return 'live';
    if (f.mode !== 'idle') return `${f.mode}${f.active ? `, r ${f.radius.toFixed(0)} m` : ''}`;
    if (f.nextIn !== null) return `next in ${f.nextIn.toFixed(0)} s`;
    return 'off';
  }

  /** Drop birds until the frame time fits. Never grows back; a reload starts fresh. */
  private adaptQuality(dtMs: number): void {
    // Pre-roll frames run extra steps on purpose; they say nothing about steady state.
    if (this.transition && this.transition.prerollLeft > 0) {
      this.qualityClock = 0;
      return;
    }
    this.qualityClock += dtMs;
    if (this.qualityClock < 3000) return;
    if (this.frameEma > 19 && this.sim.active > 3000) {
      this.sim.active = Math.max(3000, Math.floor(this.sim.active * 0.8));
      this.qualityClock = 0;
    }
  }

  private resize(): void {
    const dpr = Math.min(MAX_DPR, window.devicePixelRatio || 1);
    const w = Math.max(1, Math.round(this.canvas.clientWidth * dpr));
    const h = Math.max(1, Math.round(this.canvas.clientHeight * dpr));
    if (this.canvas.width !== w || this.canvas.height !== h) {
      this.canvas.width = w;
      this.canvas.height = h;
    }
  }

  // ---- flock lifecycle ---------------------------------------------------

  /** Replay starts here: sim clock to zero, seeded flock, seeded falcon schedule. */
  private resetFlock(seed: number): void {
    this.params.seed = seed >>> 0;
    this.simTime = 0;
    this.accumulator = 0;
    this.predator.reset(seed);
    this.falcon = null;
    this.flockCenter = null;
    this.sawAttack = false;
    this.sim.reinit(seed, this.stepUniforms(this.frameContext(), null));
  }

  private rebuildSim(): void {
    this.sim.dispose();
    this.sim = new FlockSim(this.ctx, Math.round(this.params.count), this.params.seed);
    this.resetFlock(this.params.seed);
    this.qualityClock = 0;
  }

  private reseed(): void {
    this.resetFlock(randomSeed());
    this.scheduleUrlUpdate();
  }

  private setParam(key: NumericParamKey, value: number): void {
    this.params[key] = value;
    if (key === 'count') this.rebuildSim();
    // Touching the light makes it the user's own; it stops following the photo.
    if (key === 'lightAngle' || key === 'lightWarmth') this.params.lightAuto = 0;
    this.scheduleUrlUpdate();
  }

  /** Store a value exactly as the permalink will carry it, so the scene and its link agree. */
  private setSnapped(key: NumericParamKey, value: number): void {
    this.params[key] = snap(PARAM_BY_KEY.get(key)!, value);
  }

  private setFlag(flag: number, on: boolean): void {
    this.params.flags = on ? this.params.flags | flag : this.params.flags & ~flag;
    this.applyVisibility();
    this.scheduleUrlUpdate();
  }

  private togglePause(): boolean {
    this.paused = !this.paused;
    this.panel.setPaused(this.paused);
    return this.paused;
  }

  // ---- photos ------------------------------------------------------------

  private changePhoto(id: string, o: { resetDefaults: boolean; reseed: boolean; seconds: number }): void {
    const entry = findPhoto(this.catalog, id) ?? this.catalog.photos[0]!;
    if (this.transition && this.transition.entry.id === entry.id) return;
    if (!this.transition && this.photo?.id === entry.id && !o.reseed) {
      // Already showing it; also drop any older load still in flight so it cannot land later.
      this.photoLoading = null;
      return;
    }
    this.photoLoading = entry.id;
    const horizon = o.resetDefaults ? entry.horizon : this.params.horizon;
    void loadPhoto(this.gl, entry.id, entry.file, horizon)
      .then((photo) => {
        if (this.photoLoading !== entry.id) {
          this.gl.deleteTexture(photo.tex);
          return;
        }
        // A transition already running is cut short: its target becomes the "from".
        if (this.transition) this.finishTransition();
        this.transition = {
          started: performance.now(),
          seconds: o.seconds,
          from: this.photo,
          to: photo,
          entry,
          resetDefaults: o.resetDefaults,
          reseed: o.reseed,
          switched: false,
          prerollLeft: 0,
        };
      })
      .catch((err: unknown) => {
        console.error(err);
        if (this.photoLoading === entry.id) this.photoLoading = null;
        // The picker already shows the new photo; snap it back to what is on screen.
        this.panel.refresh();
        this.panel.showToast(t('photoFailed'));
      });
  }

  private updateTransition(now: number): void {
    const tr = this.transition;
    if (!tr) return;
    const u = (now - tr.started) / (tr.seconds * 1000);
    if (u < 0.4) {
      this.birdAlpha = 1 - u / 0.4;
      return;
    }
    if (!tr.switched) this.switchTransition(tr);
    if (u < 1) {
      this.birdAlpha = 0;
      return;
    }
    this.birdAlpha = clamp(((u - 1) * tr.seconds) / BIRDS_BACK_S, 0, 1);
    if (this.birdAlpha >= 1) this.finishTransition();
  }

  /** Birds are invisible now: swap the scene underneath them. */
  private switchTransition(tr: Transition): void {
    tr.switched = true;
    this.photo = tr.to;
    this.params.photo = tr.entry.id;
    if (tr.resetDefaults) this.applyPhotoDefaults(tr.to, tr.entry, true);
    else if (this.params.lightAuto >= 0.5) this.applyLight(tr.to);
    this.resetFlock(tr.reseed ? randomSeed() : this.params.seed);
    tr.prerollLeft = PREROLL_STEPS;
    this.panel.refresh();
    this.scheduleUrlUpdate();
  }

  private finishTransition(): void {
    const tr = this.transition;
    if (!tr) return;
    if (!tr.switched) this.switchTransition(tr);
    if (tr.from && tr.from !== this.photo) this.gl.deleteTexture(tr.from.tex);
    this.transition = null;
    this.birdAlpha = 1;
  }

  /** Horizon and light as the photo suggests them; the light keeps following the photo. */
  private applyPhotoDefaults(photo: Photo, entry: CatalogPhoto, horizon: boolean): void {
    if (horizon) this.setSnapped('horizon', entry.horizon);
    this.params.lightAuto = 1;
    this.applyLight(photo);
  }

  private applyLight(sky: SkyAnalysis): void {
    this.setSnapped('lightAngle', sky.lightAngle);
    this.setSnapped('lightWarmth', sky.lightWarmth);
  }

  /**
   * After a horizon drag: re-read the sky from the same image. The haze colour
   * always follows; the light only while it still follows the photo.
   */
  private reanalyseSky(): void {
    if (!this.photo) return;
    const sky = analyzeSky(this.photo.img, this.params.horizon);
    Object.assign(this.photo, sky);
    if (this.params.lightAuto >= 0.5) this.applyLight(sky);
    this.panel.refresh();
    this.scheduleUrlUpdate();
  }

  // ---- kiosk -------------------------------------------------------------

  private get kiosk(): boolean {
    return (this.params.flags & Flags.kiosk) !== 0;
  }

  private get liveAllowed(): boolean {
    return (this.params.flags & Flags.liveAllowed) !== 0;
  }

  /** gesture: entered by a button or key, so fullscreen is allowed and a hint makes sense. */
  private setKiosk(on: boolean, gesture = false): void {
    if (on === this.kiosk) return;
    this.params.flags = on ? this.params.flags | Flags.kiosk : this.params.flags & ~Flags.kiosk;
    this.kioskClock = 0;
    document.body.classList.toggle('kiosk', on);
    if (on) {
      this.horizonTool.stop();
      this.panelForced = false;
      if (this.paused) this.togglePause();
      // The link is the start state; write it now, then leave it alone while cycling.
      this.writeUrlNow();
      if (gesture) {
        this.panel.showToast(this.liveAllowed ? (isTouchDevice() ? t('kioskHintTouch') : t('kioskHintMouse')) : t('kioskHintTap'), 3500);
        if (document.fullscreenEnabled && !document.fullscreenElement) {
          this.enteredFullscreen = true;
          document.documentElement.requestFullscreen().catch(() => (this.enteredFullscreen = false));
        }
      }
      this.armIdleCursor();
      void this.requestWakeLock();
    } else {
      document.body.classList.remove('cursor-hidden');
      void this.releaseWakeLock();
      if (this.enteredFullscreen && document.fullscreenElement) void document.exitFullscreen().catch(() => undefined);
      this.enteredFullscreen = false;
      this.writeUrlNow();
    }
    this.applyVisibility();
  }

  private tickKiosk(dtMs: number): void {
    this.kioskClock += dtMs;
    if (this.kioskClock < KIOSK_INTERVAL_MS || this.transition) return;
    this.kioskClock = 0;
    const ids = this.catalog.photos.map((p) => p.id);
    if (ids.length < 2) {
      this.resetFlock(randomSeed());
      return;
    }
    const next = ids[(ids.indexOf(this.params.photo) + 1) % ids.length]!;
    this.changePhoto(next, { resetDefaults: true, reseed: true, seconds: CROSSFADE_KIOSK_S });
  }

  private onFullscreenChange(): void {
    // Leaving fullscreen with Esc while in kiosk would strand the visitor without UI.
    if (!document.fullscreenElement && this.kiosk && this.enteredFullscreen) this.setKiosk(false);
  }

  private async requestWakeLock(): Promise<void> {
    const wl = (navigator as Navigator & { wakeLock?: { request(type: 'screen'): Promise<WakeLockLike> } }).wakeLock;
    // The browser releases the lock itself whenever the page is hidden; a held
    // sentinel that is already released must not block re-acquiring one.
    if (!wl || (this.wakeLock && !this.wakeLock.released) || document.visibilityState !== 'visible') return;
    try {
      const s = await wl.request('screen');
      s.addEventListener('release', () => {
        if (this.wakeLock === s) this.wakeLock = null;
      });
      if (!this.kiosk) {
        // Kiosk ended while the request was in flight.
        void s.release();
        return;
      }
      this.wakeLock = s;
    } catch {
      this.wakeLock = null;
    }
  }

  private async releaseWakeLock(): Promise<void> {
    const w = this.wakeLock;
    this.wakeLock = null;
    if (w) await w.release().catch(() => undefined);
  }

  // ---- cursor and visibility --------------------------------------------

  private armIdleCursor(): void {
    window.clearTimeout(this.cursorTimer);
    document.body.classList.remove('cursor-hidden');
    const ms = this.kiosk ? KIOSK_IDLE_MS : IDLE_CURSOR_MS;
    this.cursorTimer = window.setTimeout(() => {
      if (this.kiosk || (!this.panel.isOpen && this.livePointer === null && !this.horizonTool.isActive)) {
        document.body.classList.add('cursor-hidden');
      }
    }, ms);
  }

  private onAnyPointerMove(): void {
    this.armIdleCursor();
  }

  /** Panel and HUD follow the flags, the expert switch and the session overrides. */
  private applyVisibility(): void {
    const hideControls = (this.params.flags & Flags.hideControls) !== 0;
    this.panel.setHidden(this.kiosk || (hideControls && !this.panelForced));
    this.hud.setVisible(this.hudOn && this.panel.isExpert && !this.kiosk);
    this.canvas.classList.toggle('live', this.liveAllowed);
  }

  private onExpert(expert: boolean): void {
    // The overlay comes on the first time expert mode does, then keeps its own switch.
    if (expert && localStorage_get(HUD_KEY) === null) this.setHud(true);
    this.applyVisibility();
  }

  private setHud(on: boolean): void {
    this.hudOn = on;
    writeFlag(HUD_KEY, on);
    this.applyVisibility();
  }

  private toggleFullscreen(): void {
    if (!document.fullscreenEnabled) return;
    if (document.fullscreenElement) void document.exitFullscreen();
    else void document.documentElement.requestFullscreen().catch(() => undefined);
  }

  // ---- live falcon -------------------------------------------------------

  private onCanvasDown(e: PointerEvent): void {
    if (e.button !== 0 || this.horizonTool.isActive) return;
    this.lastPointerType = e.pointerType;
    this.pointersDown.add(e.pointerId);
    if (this.kiosk) {
      if (!this.liveAllowed) {
        // Screensaver semantics: any touch wakes it up.
        this.setKiosk(false);
        return;
      }
      if (this.pointersDown.size >= 2) {
        // Two-finger tap leaves kiosk; the falcon under the first finger goes too.
        this.releaseLive();
        this.setKiosk(false);
        return;
      }
    }
    if (!e.isPrimary) return;
    if (this.panel.isOpen) {
      // Tapping outside the panel only dismisses it; never a falcon on the same tap.
      this.panel.setOpen(false);
      return;
    }
    if (!this.liveAllowed) return;
    this.livePointer = e.pointerId;
    this.liveStart = performance.now();
    if (e.pointerType === 'mouse') document.body.classList.add('dragging');
    try {
      this.canvas.setPointerCapture(e.pointerId);
    } catch {
      /* not fatal */
    }
    this.setLiveFromEvent(e);
  }

  private onCanvasMove(e: PointerEvent): void {
    if (e.pointerId === this.livePointer) this.setLiveFromEvent(e);
  }

  private onCanvasUp(e: PointerEvent): void {
    this.pointersDown.delete(e.pointerId);
    if (e.pointerId !== this.livePointer) return;
    if (performance.now() - this.liveStart >= HOLD_FOR_HINT_MS) this.markHinted();
    this.releaseLive();
  }

  private releaseLive(): void {
    this.livePointer = null;
    document.body.classList.remove('dragging');
    this.predator.setLive(null);
  }

  private setLiveFromEvent(e: PointerEvent): void {
    const r = this.canvas.getBoundingClientRect();
    const sx = (e.clientX - r.left) / Math.max(1, r.width);
    // A fingertip covers the spot; put the falcon just above it where it can be seen.
    const offset = e.pointerType === 'touch' ? TOUCH_OFFSET_PX / Math.max(1, r.height) : 0;
    const sy = (e.clientY - r.top) / Math.max(1, r.height) - offset;
    const fc = this.frameContext();
    // The plane the finger lands on passes through the birds, not the nominal anchor.
    this.predator.setLive(pointerToWorld(fc.cam, sx, sy, this.flockCenter ?? fc.anchor));
  }

  /**
   * One hint, when it can land: after the first attack has shown what a
   * falcon does, or after ten seconds when there are no scheduled attacks.
   * Repeats on later visits until the visitor has actually held once.
   */
  private maybeShowHint(): void {
    if (this.hintShown || this.hinted || this.kiosk || !this.liveAllowed) return;
    const ready = this.sawAttack || (this.params.predatorRate <= 0.02 && this.simTime > 10);
    if (!ready) return;
    this.hintShown = true;
    this.panel.showToast(isTouchDevice() ? t('liveHintTouch') : t('liveHintMouse'), 5000);
  }

  private markHinted(): void {
    this.hinted = true;
    writeFlag(HINT_KEY, true);
  }

  initHint(): void {
    this.hinted = readFlag(HINT_KEY, false);
    if (!this.hinted) {
      const visits = Number(localStorage_get(VISITS_KEY) ?? '0') + 1;
      try {
        localStorage.setItem(VISITS_KEY, String(visits));
      } catch {
        /* ignore */
      }
      if (visits > 3) this.hinted = true;
    }
  }

  // ---- horizon, share, URL -----------------------------------------------

  private startHorizon(): void {
    this.panel.setOpen(false);
    this.horizonTool.start(photoToScreenY(this.frameContext().fit, this.params.horizon));
  }

  private async share(): Promise<boolean> {
    const url = this.currentUrl();
    history.replaceState(null, '', url);
    try {
      if (navigator.share && /Mobi|Android/i.test(navigator.userAgent)) {
        await navigator.share({ url, title: t('title') });
        return true;
      }
      await navigator.clipboard.writeText(url);
      return true;
    } catch {
      return false;
    }
  }

  private currentUrl(): string {
    return sceneUrl(location.origin, encodeScene(this.params), 'hash');
  }

  /** Debounced. Frozen while kiosk cycles, so the link stays the start state. */
  private scheduleUrlUpdate(): void {
    window.clearTimeout(this.urlTimer);
    if (this.kiosk) return;
    this.urlTimer = window.setTimeout(() => this.writeUrlNow(), 300);
  }

  private writeUrlNow(): void {
    window.clearTimeout(this.urlTimer);
    const url = this.currentUrl();
    if (location.href !== url) history.replaceState(null, '', url);
  }

  private onHashChange(): void {
    const code = sceneCodeFromLocation(location);
    if (!code) return;
    try {
      const next = decodeScene(code);
      if (encodeScene(next) === encodeScene(this.params)) return;
      const wasKiosk = this.kiosk;
      const photoChanged = next.photo !== this.params.photo;
      const countChanged = Math.round(next.count) !== Math.round(this.params.count);
      Object.assign(this.params, next);
      if (countChanged) this.rebuildSim();
      else this.resetFlock(this.params.seed);
      if (photoChanged) this.changePhoto(this.params.photo, { resetDefaults: false, reseed: false, seconds: CROSSFADE_USER_S });
      this.panel.refresh();
      // Route a kiosk flag change through setKiosk so cursor, wake lock and
      // fullscreen bookkeeping are set up or torn down in both directions.
      const nowKiosk = this.kiosk;
      if (nowKiosk !== wasKiosk) {
        this.params.flags = wasKiosk ? this.params.flags | Flags.kiosk : this.params.flags & ~Flags.kiosk;
        this.setKiosk(nowKiosk);
      }
      this.applyVisibility();
    } catch {
      this.panel.showToast(t('badLink'));
    }
  }

  private onKey(e: KeyboardEvent): void {
    if (e.target instanceof HTMLInputElement || e.target instanceof HTMLSelectElement) return;
    // A focused panel control owns Space and Enter; only Escape falls through.
    if ((e.target instanceof HTMLButtonElement || e.target instanceof HTMLAnchorElement) && e.key !== 'Escape') return;
    if (e.metaKey || e.ctrlKey || e.altKey) return;
    if (this.kiosk) {
      // Anything deliberate leaves kiosk; a remote's arrow nudge or a modifier does not.
      const deliberate = e.key === 'Escape' || e.key === 'Enter' || e.key === ' ' || e.key.length === 1;
      if (deliberate) {
        e.preventDefault();
        this.setKiosk(false);
      }
      return;
    }
    switch (e.key) {
      case ' ':
        e.preventDefault();
        this.togglePause();
        break;
      case 'h':
        if (this.horizonTool.isActive) this.horizonTool.stop();
        else this.startHorizon();
        break;
      case 'r':
        this.reseed();
        break;
      case 'k':
        this.setKiosk(true, true);
        break;
      case 'f':
        this.toggleFullscreen();
        break;
      case 'c':
        this.panelForced = !this.panelForced;
        this.applyVisibility();
        break;
      case 'i':
        this.setHud(!this.hudOn);
        break;
      case 'd':
        if (this.panel.isExpert) this.debug = !this.debug;
        break;
      case 'Escape':
        this.horizonTool.stop();
        this.panel.setOpen(false);
        break;
      default:
        return;
    }
  }
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, v));
}
function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}
function smoothstep(t: number): number {
  const x = clamp(t, 0, 1);
  return x * x * (3 - 2 * x);
}
interface WakeLockLike {
  readonly released: boolean;
  release(): Promise<void>;
  addEventListener(type: 'release', listener: () => void): void;
}
function localStorage_get(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}
function readFlag(key: string, fallback: boolean): boolean {
  const v = localStorage_get(key);
  return v === null ? fallback : v === '1';
}
function writeFlag(key: string, on: boolean): void {
  try {
    localStorage.setItem(key, on ? '1' : '0');
  } catch {
    /* ignore */
  }
}

function fail(msg: string): void {
  const el = document.getElementById('message')!;
  el.textContent = msg;
  el.hidden = false;
}

async function boot(): Promise<void> {
  setLang(lang());
  const canvas = document.getElementById('c') as HTMLCanvasElement;
  const ctx = initGL(canvas);
  if (!ctx) {
    fail(t('noWebGL'));
    return;
  }
  const catalog = await loadCatalog();
  let params = defaultParams();
  let badLink = false;
  const code = sceneCodeFromLocation(location);
  if (code) {
    try {
      params = decodeScene(code);
    } catch {
      badLink = true;
    }
  }
  const photo = findPhoto(catalog, params.photo) ?? catalog.photos[0]!;
  params.photo = photo.id;
  const fromLink = !!code && !badLink;
  if (!fromLink) {
    params.horizon = snap(PARAM_BY_KEY.get('horizon')!, photo.horizon);
    params.seed = randomSeed();
  }
  document.body.classList.toggle('kiosk', (params.flags & Flags.kiosk) !== 0);
  const app = new App(ctx, catalog, params);
  app.initHint();
  (window as unknown as { __murm: unknown }).__murm = { app, params, gl: ctx.gl };
  await app.start(fromLink);
  if (badLink) fail(t('badLink'));
}

boot().catch((err: unknown) => {
  console.error(err);
  fail(err instanceof Error ? err.message : String(err));
});
